import { assert } from '../app/logger.js';
import { Coords } from './coords.js';

const kGradientMax = 255;

/**
 * A track created from gpx data.
 */
class GpxTrack {
  constructor(options) {
    assert(options, `[map:track] no options is given for GfxTrack`);
    this.#options = options;
  }

  get name() {
    return this.#options.name;
  }

  get collection() {
    return this.#options.collection;
  }

  get path() {
    // Path contains extra data like elevation unlike polyline path.
    return this.#options.path;
  }

  set path(v) {
    this.#options.path = v;
    if (this.#polylines) {
      this.#draw();
    }
  }

  get bounds() {
    const Map =
      this.#options && this.#options.map && this.#options.map.constructor;
    return (Map && Map.boundsFor(this.path)) || {};
  }

  get active() {
    return this.#active;
  }

  set active(v) {
    if (this.#active == !!v) {
      return;
    }

    this.#active = !!v;
    if (this.#polylines) {
      this.#draw();
      this.#options.map.fire({
        type: v ? 'trackactive' : 'trackinactive',
        track: this,
      });
    }
  }

  get interactive() {
    return this.#options.clickable;
  }

  set interactive(v) {
    if (this.#options.clickable == v) {
      return;
    }

    this.#options.clickable = v;
    this.#setActivateOnClickListeners(v);
  }

  get gradientType() {
    return this.#options.gradientType;
  }

  set gradientType(type) {
    this.#options.gradientType = type;
    if (this.#polylines) {
      this.#draw();
    }
  }

  show() {
    if (!this.#polylines) {
      this.#draw();
    }
  }

  hide() {
    this.active = false;
    this.#polylines && this.#polylines.forEach(polyline => polyline.deattach());
  }

  isShown() {
    return (
      this.#polylines && this.#polylines.some(polyline => polyline.visible)
    );
  }

  getCenterPos() {
    let polyline =
      this.#polylines &&
      this.#polylines[Math.floor(this.#polylines.length / 2)];
    return polyline && polyline.getCenterPos();
  }

  splitPath(splitPoint) {
    let path = this.path;
    let closestIndex = 0;
    for (let i = 1; i < path.length; i++) {
      let point = path[i];
      if (
        Coords.distanceBetween(point, splitPoint) <
        Coords.distanceBetween(path[closestIndex], splitPoint)
      ) {
        closestIndex = i;
      }
    }
    let closestPoint = path[closestIndex];

    let startPath = path.slice(0, closestIndex + 1);
    let endPath = [];

    // If the split point is between the path points then
    // include it into both of splitted paths.
    if (Coords.distanceBetween(splitPoint, closestPoint) > 0) {
      startPath.push({
        lat: splitPoint.lat,
        lng: splitPoint.lng,
        elevation: closestPoint.elevation,
        date: closestPoint.date,
      });
      endPath.push({
        lat: splitPoint.lat,
        lng: splitPoint.lng,
        elevation: closestPoint.elevation,
        date: closestPoint.date,
      });
    }
    endPath = endPath.concat(path.slice(closestIndex + 1));

    return [startPath, endPath];
  }

  #draw() {
    if (this.#polylines) {
      for (let polyline of this.#polylines) {
        polyline.deattach();
      }
    }
    this.#polylines = [];

    let gradientType = this.active && this.gradientType;

    // If no elevation data, then use default style.
    if (
      gradientType == 'elevation' &&
      this.path.some(point => !point.elevation)
    ) {
      gradientType = '';
    }

    let style = this.#getStyle();
    switch (gradientType) {
      case 'elevation':
        for (let i = 0; i < this.path.length - 1; i++) {
          let point = this.path[i];
          let nextPoint = this.path[i + 1];
          let slope = Coords.slope(point, nextPoint);
          let color;
          if (slope > Math.PI / 11) {
            // > ~16 degrees
            color = style.gradientSteepSlopeColor;
          } else if (slope > Math.PI / 18) {
            // > 10 degrees
            color = style.gradientHighSlopeColor;
          } else if (slope > Math.PI / 30) {
            // > ~6 degrees
            color = style.gradientMediumHighSlopeColor;
          } else if (slope > Math.PI / 60) {
            // > 3 degrees
            color = style.gradientMediumSlopeColor;
          } else if (slope > Math.PI / 180) {
            // > 1 degrees
            color = style.gradientLowMediumSlopeColor;
          } else {
            color = style.gradientLowSlopeColor;
          }
          let polyline = new window.MapPolyline(
            Object.assign({}, this.#options, {
              path: [point, nextPoint],
              color,
              weight: style.gradientSlopeWeight,
            })
          );
          polyline.attachTo(this.#options.map);
          this.#polylines.push(polyline);
        }
        break;

      case 'time':
        {
          let gradientStep = kGradientMax / this.path.length;
          let pointStep = Math.ceil(this.path.length / kGradientMax);
          let [startR, startG, startY] = style.activeGradientStartColor
            .match(/\w{2}/g)
            .map(color => parseInt(color, 16));
          let [endR, endG, endY] = style.activeGradientEndColor
            .match(/\w{2}/g)
            .map(color => parseInt(color, 16));

          for (let i = 0; i < this.path.length - pointStep; i += pointStep) {
            let r = this.#getGradient(i * gradientStep, startR, endR);
            let g = this.#getGradient(i * gradientStep, startG, endG);
            let y = this.#getGradient(i * gradientStep, startY, endY);
            let color = `#${r}${g}${y}`;
            let polyline = new window.MapPolyline(
              Object.assign({}, this.#options, {
                path: this.path.slice(
                  i,
                  i + pointStep >= this.path.length
                    ? this.path.length
                    : i + pointStep + 1
                ),
                color,
                weight: style.activeWeight,
              })
            );
            polyline.attachTo(this.#options.map);
            this.#polylines.push(polyline);
          }
        }
        break;

      default:
        this.#polylines.push(new window.MapPolyline(this.#currentOptions));
        this.#polylines[0].attachTo(this.#options.map);
        break;
    }

    if (this.interactive) {
      this.#setActivateOnClickListeners(true);
    }
  }

  get #currentOptions() {
    return Object.assign(
      {},
      this.#options,
      this.active ? this.#activeStyle : this.#defaultStyle
    );
  }

  #setActivateOnClickListeners(enable) {
    if (enable) {
      this.#activateOnClickListeners = this.#polylines.map(polyline =>
        polyline.on('click', ev => {
          ev.stop();
          this.#options.map.fire({
            type: 'trackclick',
            track: this,
            lat: ev.lat,
            lng: ev.lng,
          });
          this.active = true;
        })
      );
    } else {
      for (let i = 0; i < this.#polylines.length; i++) {
        this.#polylines[i].off('click', this.#activateOnClickListeners[i]);
      }
    }
  }

  get #defaultStyle() {
    let style = this.#getStyle();
    return {
      weight: style.defaultWeight,
      color: style.defaultColor,
    };
  }

  get #activeStyle() {
    let style = this.#getStyle();
    return {
      weight: style.activeWeight,
      color: style.activeColor,
    };
  }

  #getStyle() {
    return new (class {
      get defaultWeight() {
        return this.#s.getPropertyValue('--map-track-default-weight');
      }
      get defaultColor() {
        return this.#s.getPropertyValue('--map-track-default-color');
      }
      get activeWeight() {
        return this.#s.getPropertyValue('--map-track-active-weight');
      }
      get activeColor() {
        return this.#s.getPropertyValue('--map-track-active-color');
      }
      get activeGradientStartColor() {
        return this.#s.getPropertyValue(
          '--map-track-active-gradient-start-color'
        );
      }
      get activeGradientEndColor() {
        return this.#s.getPropertyValue(
          '--map-track-active-gradient-end-color'
        );
      }
      get gradientSlopeWeight() {
        return this.#s.getPropertyValue('--map-track-slope-weight');
      }
      get gradientLowSlopeColor() {
        return this.#s.getPropertyValue('--map-track-low-slope-color');
      }
      get gradientLowMediumSlopeColor() {
        return this.#s.getPropertyValue('--map-track-low-medium-slope-color');
      }
      get gradientMediumSlopeColor() {
        return this.#s.getPropertyValue('--map-track-medium-slope-color');
      }
      get gradientMediumHighSlopeColor() {
        return this.#s.getPropertyValue('--map-track-medium-high-slope-color');
      }
      get gradientHighSlopeColor() {
        return this.#s.getPropertyValue('--map-track-high-slope-color');
      }
      get gradientSteepSlopeColor() {
        return this.#s.getPropertyValue('--map-track-steep-slope-color');
      }
      #s = window.getComputedStyle(document.body);
    })();
  }

  #getGradient(curGradient, start, end) {
    let c = (
      Math.floor(
        (curGradient / kGradientMax) * end +
          (1 - curGradient / kGradientMax) * start +
          256
      ) % 256
    ).toString(16);
    return c.length == 1 ? `0${c}` : c;
  }

  #active = false;
  #activateOnClickListeners = [];
  #options;
  #polylines;
}

export { GpxTrack };
